import { QueryReturnValue } from "@reduxjs/toolkit/dist/query/baseQueryTypes";
import {
  createApi,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query/react";
import { API_HEADERS, ENDPOINTS } from "../../constants/api";
import { baseQueryWithAuthCheck } from "./helper";
import {
  CandidatePipeline,
  FetchManageFolderListResponse,
  GetPollingResponse,
  ManageFoldersCountResponse,
  ProfileCountResponse,
  RenameFolderResponse,
} from "../../types/api-response";
import {
  ArchiveFolderPayload,
  FolderOptionType,
  GetCampaignListPayload,
  GetPollingPayload,
  GetShareFolderRecDetailsPaylod,
  OutreactCampaignDataRes,
  PauseCampaignPayload,
  ProfilesContactedPayload,
  ProfilesContactedRes,
  RenameFolderPayload,
  ShareFolderPayload,
  TemplateType,
  fetchManageFolderPaylod,
} from "@/types/api-payload";
import { callFromMiddleware } from "@/utils/index";
import { FETCH_FOLDER, FETCH_REC_DETAILS } from "./tags";
import { ManageFolderAPIRecShareRes } from "@/types/index";

const baseQuery = fetchBaseQuery({
  baseUrl: window.location.origin,
  headers: API_HEADERS,
  credentials: "include",
});

export const manageFolderApi = createApi({
  reducerPath: "manageFolder",
  baseQuery: baseQueryWithAuthCheck(baseQuery),
  tagTypes: [FETCH_FOLDER, FETCH_REC_DETAILS],
  endpoints: (builder) => ({
    fetchManageFolders: builder.query<
      FetchManageFolderListResponse,
      Partial<fetchManageFolderPaylod>
    >({
      query: (body: any) => ({
        url: body.url,
        method: "POST",
        headers: API_HEADERS,
        body: {},
      }),
      providesTags: [FETCH_FOLDER],
    }),
    getManageFoldersCount: builder.query<
      Partial<ManageFoldersCountResponse>,
      { folderName: string }
    >({
      query: (args) => ({
        url: callFromMiddleware(ENDPOINTS.getManageFoldersCount),
        method: "POST",
        headers: API_HEADERS,
        params: args,
        body: {},
      }),
      providesTags: [FETCH_FOLDER],
    }),
    getApplyReplyCount: builder.query<any,any>({
      query: (args) => ({
        url: callFromMiddleware(ENDPOINTS.getApplyReplyCount),
        method: "POST",
        headers: API_HEADERS,
        body: args,
      }),
      providesTags: [FETCH_FOLDER],
    }),
    getProfilesCount: builder.query<
    Partial<ProfileCountResponse>,
    { folderId: number }
  >({
    query: (args) => ({
      url: callFromMiddleware(ENDPOINTS.getProfilesCount),
      method: "POST",
      headers: API_HEADERS,
      params: args,
      body: {},
    }),
  }),
    getCandidatePipelineCount: builder.query<
      Partial<CandidatePipeline>,
      { folderId: number }
    >({
      query: (args) => ({
        url: callFromMiddleware(
          `${ENDPOINTS.getCandidatePipelineCount}/${args?.folderId}`
        ),
        method: "POST",
        headers: API_HEADERS,
        params: {},
        body: {},
      }),
    }),
    getDownloadProfilesPolling: builder.query<
      any,
      any
    >({
      query: (args) => ({
        url: callFromMiddleware(ENDPOINTS.getDownloadProfilesPolling),
        method: "POST",
        headers: API_HEADERS,
        params: {},
        body: args,
      }),
    }),

    renameFolder: builder.mutation<RenameFolderResponse, RenameFolderPayload>({
      query: (args: any) => ({
        url: `${callFromMiddleware(ENDPOINTS.renameManageFolder)}/${
          args.folderId
        }`,
        method: "POST",
        headers: API_HEADERS,
        params: {
          updateName: args.updatedFolderName,
        },
        body: {},
      }),
      // refetch only on successfull api call
      invalidatesTags: (result: any, error: any) =>
        error ? [] : [FETCH_FOLDER],
    }),
    getShareFolderRecDetails: builder.query<
      ManageFolderAPIRecShareRes,
      GetShareFolderRecDetailsPaylod
    >({
      query: (args: any) => ({
        url: `${callFromMiddleware(ENDPOINTS.getManageFolderRec)}/recruiters`,
        method: "POST",
        headers: API_HEADERS,
        params: { folderId: args.folderId, searchName: args.searchName },
        body: {},
      }),
      providesTags: [FETCH_REC_DETAILS],
    }),
    shareFolder: builder.mutation<number, ShareFolderPayload>({
      query: (args: any) => ({
        url: callFromMiddleware(ENDPOINTS.manageFolderShare),
        method: "POST",
        headers: API_HEADERS,
        params: { folderId: args.folderId, share: args.share },
        body: args.subUid,
      }),
      invalidatesTags: (result: any, error: any) =>
        error ? [] : [FETCH_FOLDER],
    }),
    archiveFolder: builder.mutation<void, ArchiveFolderPayload>({
      query: (args: any) => ({
        url: `${callFromMiddleware(ENDPOINTS.manageFolderArchive)}/${
          args.action
        }/${args.folderId}`,
        method: "POST",
        headers: API_HEADERS,
        body: {},
      }),
      invalidatesTags: [FETCH_FOLDER],
    }),
    fetchManageFolderCampaignList: builder.query<
      OutreactCampaignDataRes,
      GetCampaignListPayload
    >({
      query: (args) => ({
        url: callFromMiddleware(ENDPOINTS.fetchManageFolderCampaigns),
        method: "POST",
        headers: API_HEADERS,
        body: { folderIds: args.folderIds },
      }),
    }),
    pauseManageFolderCampaign: builder.query<
      OutreactCampaignDataRes,
      PauseCampaignPayload
    >({
      query: (args) => ({
        url: `${callFromMiddleware(ENDPOINTS.pauseCampaign)}/${args.id}`,
        method: "GET",
        headers: API_HEADERS,
      }),
    }),
    getUUIDS: builder.query<
      any,
      any
    >({
      query: (args) => ({
        url: `${callFromMiddleware(ENDPOINTS.getUUIDS)}?folderId=${args.folderId}`,
        method: "GET",
        headers: API_HEADERS,
      }),
    }),
    // redirectToCampaign: builder.mutation({
    //   query: (payload: any) => ({
    //     url: callFromMiddleware(ENDPOINTS.redirectToCampaign),
    //     method: 'POST',
    //     headers: API_HEADERS,
    //     body: payload
    //   }),
    // }),

    // fetchFolders: builder.query<FolderOptionType[],
    //   void
    // >({
    //   query: () => ({
    //     url: callFromMiddleware(ENDPOINTS.fetchFolders),
    //     method: 'GET',
    //     headers: API_HEADERS,
    //   }),
    // }),

    // fetchTemplates: builder.query<TemplateType[],
    //   void
    // >({
    //   query: () => ({
    //     url: callFromMiddleware(ENDPOINTS.fetchSMSTemplates),
    //     method: 'GET',
    //     headers: API_HEADERS,
    //   }),
    // }),
    // sendSMS: builder.mutation({
    //   query: (payload) => ({
    //     url: callFromMiddleware(ENDPOINTS.sendSMS),
    //     method: 'POST',
    //     headers: API_HEADERS,
    //     body: payload
    //   })
    // }),
    // cloneProfile: builder.mutation({
    //   query: (payload) => ({
    //     url: callFromMiddleware(ENDPOINTS.cloneProfile),
    //     method: 'POST',
    //     headers: API_HEADERS,
    //     body: payload
    //   }),
    //   invalidatesTags: (result: any, error: any) => (error ? [] : [FETCH_FOLDER]),
    // }),
    // removeProfile: builder.mutation({
    //   query: (payload) => ({
    //     url: callFromMiddleware(ENDPOINTS.removeProfile),
    //     method: 'POST',
    //     headers: API_HEADERS,
    //     body: payload
    //   }),
    //   invalidatesTags: (result: any, error: any) => (error ? [] : [FETCH_FOLDER]),
    // }),
    downloadExcel: builder.query({
      query: (args) => ({
        url: callFromMiddleware(ENDPOINTS.downloadExcel),
        method: "GET",
        headers: {
          ...API_HEADERS,
          "Content-Type":
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "Content-Disposition": "attachment",
          Accept:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
        params: args,
      }),
    }),
    inventory: builder.query({
      query: (args) => ({
        url: callFromMiddleware(ENDPOINTS.inventory),
        method: "POST",
        body: args,
        headers: API_HEADERS,
        refetchOnMountOrArgChange: true,
      }),
    }),
    linkedJob: builder.query({
      query: (args) => ({
        url: callFromMiddleware(ENDPOINTS.linkedJob),
        method: "POST",
        body: {},
        params: args,
        headers: API_HEADERS,
        // refetchOnMountOrArgChange: true,
      }),
    }),
    getAllAssessments: builder.query<any,any>({
      query: (args) => ({
        url: callFromMiddleware(ENDPOINTS.getAllAssessments),
        method: "GET",
        headers: API_HEADERS,
        params: args,
      }),
    }),
    getAssessmentCount: builder.query<any,any>({
      query: ({folderId}) => ({
        url: `${callFromMiddleware(ENDPOINTS.getAssessmentCount)}/${folderId}`,
        method: "GET",
        headers: API_HEADERS,
      }),
    }),
    requestCredit: builder.query<any,any>({
      query: (body) => ({
        url: callFromMiddleware(ENDPOINTS.requestCredit),
        method: "POST",
        headers: API_HEADERS,
        body,
      }),
    }),
    requestCustomCredit: builder.mutation<any,any>({
      query: (body) => ({
        url: callFromMiddleware(ENDPOINTS.customRequest),
        method: "POST",
        headers: API_HEADERS,
        body,
      }),
    }),
    initiateAssessment: builder.query<any,any>({
      query: (body) => ({
        url: callFromMiddleware(ENDPOINTS.initiateAssessment),
        method: "POST",
        headers: API_HEADERS,
        body,
      }),
    }),
    getCandidateDetails: builder.query<any, any>({
      query: ({page, candidateId, campaignId, folderId, candidateName, filter, sortBy }) => ({
        url: `${callFromMiddleware(
          ENDPOINTS.getCandidateDetails
        )}/${page}`,
        method: "GET",
        params: {
          campaignId,
          folderId,
          candidateName,
          filter,
          sortBy,
        },
        headers:API_HEADERS
      }),
    }),
    getAssessmentDetails: builder.query<any, any>({
      query: ({ positionId }) => ({
        url: `${callFromMiddleware(ENDPOINTS.getAssessmentDetails)}`,
        method: "GET",
        params: {
          positionId,
        },
        headers: API_HEADERS
      }),
    }),
    getAssessmentNames: builder.query<any, any>({
      query: ({ folderId }) => ({
        url: `${callFromMiddleware(ENDPOINTS.getAssessmentNames)}/${folderId}`,
        method: "GET",
        headers: API_HEADERS
      }),
    }),
    getCandidateCount: builder.query<any, any>({
      query: ({ campaignId, folderId }) => ({
        url: `${callFromMiddleware(ENDPOINTS.getCandidateCount)}`,
        method: "GET",
        params: {
          campaignId,
          folderId,
        },
        headers: API_HEADERS
      }),
    }),
    sendAssessment: builder.mutation<any,any>({
      query: (body) => ({
        url: callFromMiddleware(ENDPOINTS.sendAssessment),
        method: "POST",
        headers: API_HEADERS,
        body,
      }),
    }),
    checkLimit: builder.mutation<any,any>({
      query: (body) => ({
        url: callFromMiddleware(ENDPOINTS.checkLimit),
        method: "POST",
        headers: API_HEADERS,
        body,
      }),
    }),
    cancelInvite: builder.mutation<any,any>({
      query: (body) => ({
        url: callFromMiddleware(ENDPOINTS.cancelAssessment),
        method: "POST",
        headers: {
          ...API_HEADERS,
          Authorization: "Basic cmlwcGxlaGlyZTpYN3Z5ZFBAclQ5",
        },
        body,
      }),
    }),
    followUp: builder.mutation<any, any>({
      query: (body) => ({
        url: callFromMiddleware(ENDPOINTS.followUp),
        method: "POST",
        headers: {
          ...API_HEADERS,
          Authorization: "Basic cmlwcGxlaGlyZTpYN3Z5ZFBAclQ5",
        },
        body,
      }),
    }),
    getAssessmentRecords: builder.query<any, any>({
      query: ({ campaignId, folderId, candidateName, filter, sortBy }) => ({
        url: callFromMiddleware(ENDPOINTS.assessmentRecords),
        method: "GET",
        params: {
          campaignId,
          folderId,
          candidateName,
          filter,
          sortBy,
        },
        headers: API_HEADERS
      }),
    }),
    reportSummary: builder.mutation<any, any>({
      query: (params) => ({
        url: callFromMiddleware(ENDPOINTS.reportSummary),
        method: "POST",
        headers: API_HEADERS,
        params,
        body: {},
      }),
    }),
    allServices: builder.mutation<any, any>({
      query: (body) => ({
        url: callFromMiddleware(ENDPOINTS.allServices),
        method: "POST",
        headers: API_HEADERS,
        body,
      }),
    }),
    getBulkCandidatePipelineCount: builder.query<
      any,
      {
        folderIds:number[]
       }
    >({
      query: (body) => ({
        url: callFromMiddleware(
          `${ENDPOINTS.getBulkCandidatePipeline}`
        ),
        method: "POST",
        headers: API_HEADERS,
        params: {},
        body: body,
      }),
    }),
  }),
});

export const {
  useGetDownloadProfilesPollingQuery,
  useFetchManageFoldersQuery,
  useLazyFetchManageFoldersQuery,
  useGetManageFoldersCountQuery,
  useLazyGetProfilesCountQuery,
  useRenameFolderMutation,
  useLazyGetShareFolderRecDetailsQuery,
  useShareFolderMutation,
  useArchiveFolderMutation,
  useFetchManageFolderCampaignListQuery,
  useLazyPauseManageFolderCampaignQuery,
  useLazyDownloadExcelQuery,
  useLazyInventoryQuery,
  useLazyGetCandidatePipelineCountQuery,
  useLinkedJobQuery,
  useGetUUIDSQuery,
  useLazyGetUUIDSQuery,
  useLazyGetApplyReplyCountQuery,
  useLazyGetAllAssessmentsQuery,
  useLazyGetAssessmentCountQuery,
  useLazyRequestCreditQuery,
  useRequestCustomCreditMutation,
  useLazyInitiateAssessmentQuery,
  useLazyGetCandidateDetailsQuery,
  useLazyGetAssessmentDetailsQuery,
  useLazyGetAssessmentNamesQuery,
  useLazyGetCandidateCountQuery,
  useSendAssessmentMutation,
  useCheckLimitMutation,
  useCancelInviteMutation,
  useFollowUpMutation,
  useLazyGetAssessmentRecordsQuery,
  useReportSummaryMutation,
  useAllServicesMutation,
  useLazyGetBulkCandidatePipelineCountQuery
} = manageFolderApi;

export default manageFolderApi;
